import React, { useEffect, useState, createElement } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { MetricsCard } from '../components/admin/MetricsCard';
import { SubmissionsTable } from '../components/admin/SubmissionsTable';
import { getAnalytics, clearAnalytics } from '../utils/analytics';
import { UsersIcon, EyeIcon, MousePointerClickIcon, TrendingUpIcon, DownloadIcon, TrashIcon, RefreshCwIcon } from 'lucide-react';
interface Submission {
  timestamp: string;
  email: string;
  firstName: string;
  quizAnswers: any;
}
export function AdminPage() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [analytics, setAnalytics] = useState(getAnalytics());
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const loadData = () => {
    try {
      const data = localStorage.getItem('crowncontrol_submissions');
      if (data) {
        setSubmissions(JSON.parse(data));
      }
      setAnalytics(getAnalytics());
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const exportToCSV = () => {
    if (submissions.length === 0) {
      alert('No submissions to export');
      return;
    }
    const headers = ['Timestamp', 'Email', 'First Name', 'Age', 'Situation', 'Duration', 'Family History', 'Tried', 'Stopped Treatment', 'Symptoms', 'Commitment'];
    const rows = submissions.map(sub => [sub.timestamp, sub.email, sub.firstName || '', sub.quizAnswers.age || '', sub.quizAnswers.situation || '', sub.quizAnswers.duration || '', sub.quizAnswers.family || '', sub.quizAnswers.tried.join('; ') || '', sub.quizAnswers.stopped || '', sub.quizAnswers.symptoms || '', sub.quizAnswers.commitment || '']);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crowncontrol-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const clearAllData = () => {
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      return;
    }
    try {
      localStorage.removeItem('crowncontrol_submissions');
      clearAnalytics();
      setSubmissions([]);
      setAnalytics(getAnalytics());
      setShowClearConfirm(false);
      alert('All data cleared successfully');
    } catch (error) {
      console.error('Failed to clear data:', error);
      alert('Failed to clear data');
    }
  };
  const quizCompletionRate = analytics.pageViews.quiz > 0 ? (submissions.length / analytics.pageViews.quiz * 100).toFixed(1) : '0';
  return <div className="min-h-screen w-full bg-crown-bg flex flex-col" data-id="element-118">
      <Header data-id="element-119" />

      <main className="flex-1 py-8 sm:py-12" data-id="element-120">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-121">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-122">
            <div data-id="element-123">
              <h1 className="text-3xl font-bold text-crown-text mb-2" data-id="element-124">
                Admin Dashboard
              </h1>
              <p className="text-crown-body" data-id="element-125">
                Monitor quiz performance and submissions
              </p>
            </div>
            <div className="flex gap-3" data-id="element-126">
              <Button variant="secondary" size="sm" onClick={loadData} className="flex items-center gap-2" data-id="element-127">
                <RefreshCwIcon className="w-4 h-4" data-id="element-128" />
                Refresh
              </Button>
              <Button variant="secondary" size="sm" onClick={exportToCSV} className="flex items-center gap-2" data-id="element-129">
                <DownloadIcon className="w-4 h-4" data-id="element-130" />
                Export CSV
              </Button>
              <Button variant="secondary" size="sm" onClick={clearAllData} className="flex items-center gap-2" data-id="element-131">
                <TrashIcon className="w-4 h-4" data-id="element-132" />
                {showClearConfirm ? 'Confirm Clear?' : 'Clear All'}
              </Button>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-133">
            <MetricsCard title="Total Submissions" value={submissions.length} subtitle="Email captures" icon={<UsersIcon className="w-5 h-5" data-id="element-135" />} data-id="element-134" />
            <MetricsCard title="Landing Page Views" value={analytics.pageViews.landing} subtitle="Unique visits" icon={<EyeIcon className="w-5 h-5" data-id="element-137" />} data-id="element-136" />
            <MetricsCard title="Quiz Started" value={analytics.pageViews.quiz} subtitle={`${analytics.clicks.startQuiz} start clicks`} icon={<MousePointerClickIcon className="w-5 h-5" data-id="element-139" />} data-id="element-138" />
            <MetricsCard title="Completion Rate" value={`${quizCompletionRate}%`} subtitle={`${submissions.length} / ${analytics.pageViews.quiz} completed`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-141" />} data-id="element-140" />
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8" data-id="element-142">
            <MetricsCard title="Results Page Views" value={analytics.pageViews.results} subtitle="Post-quiz views" data-id="element-143" />
            <MetricsCard title="Next Button Clicks" value={analytics.clicks.nextQuestion} subtitle="Quiz progression" data-id="element-144" />
            <MetricsCard title="Protocol CTA Clicks" value={analytics.clicks.viewProtocol} subtitle="$497 button clicks" data-id="element-145" />
          </div>

          {/* Submissions Table */}
          <div data-id="element-146">
            <h2 className="text-2xl font-bold text-crown-text mb-4" data-id="element-147">
              Email Submissions ({submissions.length})
            </h2>
            <SubmissionsTable submissions={submissions} data-id="element-148" />
          </div>
        </div>
      </main>

      <Footer data-id="element-149" />
    </div>;
}